<template>
    <div class="tw:flex tw:flex-col tw:items-center tw:w-full tw:overflow-x-auto tw:sm:overflow-visible stepper-admin-wrapper">
        <div class="tw:flex tw:flex-col tw:items-center tw:w-full tw:mx-auto tw:text-center tw:relative">
            <div
                class="tw:flex tw:items-start tw:justify-between tw:w-full tw:max-w-[1200px] tw:relative tw:flex-nowrap stepper-admin-steps-row"
            >
                <div class="tw:absolute tw:h-[2px] tw:bg-[#ddd] tw:z-0 tw:rounded-sm stepper-admin-line-bg"></div>
                <div
                    class="tw:absolute tw:h-[2px] tw:z-[1] tw:rounded-sm stepper-admin-line-progress"
                    :style="{ '--progress-fraction': progressFraction }"
                ></div>

                <div
                    v-for="(label, index) in steps"
                    :key="index"
                    :id="'stepper-admin-step-' + (index + 1)"
                    class="tw:flex tw:flex-col tw:relative tw:z-[2]"
                    :class="{
                        'tw:items-start': index === 0,
                        'tw:items-center': index > 0 && index < steps.length - 1,
                        'tw:items-end': index === steps.length - 1,
                    }"
                >
                    <div
                        class="stepper-admin-circle tw:border-[2px] tw:rounded-full tw:flex tw:items-center tw:justify-center tw:transition-all tw:duration-300 tw:ease-in-out tw:z-[3] stepper-admin-circle--future"
                        :class="{
                            'stepper-admin-circle--active': isActiveOrDone(index),
                            'stepper-admin-circle--pulsing': isActive(index),
                        }"
                    >
                        <i v-if="isCompleted(index)" class="material-icons tw:leading-none stepper-admin-check">check</i>
                        <span v-else class="tw:leading-none stepper-admin-number">{{ index + 1 }}</span>
                    </div>
                    <span
                        class="tw:mt-2 tw:text-[#aaa] tw:font-semibold tw:transition-colors tw:duration-300 tw:ease-in-out tw:whitespace-nowrap tw:leading-[1.2] stepper-admin-label"
                        :class="{
                            'tw:!text-[#444444] tw:!font-bold': isActiveOrDone(index),
                            'tw:text-left': index === 0,
                            'tw:text-center': index > 0 && index < steps.length - 1,
                            'tw:text-right': index === steps.length - 1,
                        }"
                    >
                        {{ label }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue';
    import { useStepper } from '@/composables/admin/common/useStepper';

    const props = defineProps({
        steps:       { type: Array,  required: true },
        currentStep: { type: Number, required: true },
    });

    const emit = defineEmits(['update:currentStep']);

    const { isCompleted, isActive, isActiveOrDone } = useStepper(
        computed(() => props.steps),
        computed(() => props.currentStep),
    );

    /** Ajuste por ancho de pantalla: en pantallas estrechas la fracción debe ser mayor */
    const screenAdjust = ref(0);

    /** Factor multiplicador para compensar el ancho de los círculos.
     *  1.12 = +12% (en vez de sumar 0.025 fijo a 0.2) */
    const screenFactor = ref(1);

    function computeAdjust() {
        const w = window.innerWidth;
        if (w >= 1024) screenFactor.value = 1.0;          // laptop/desktop: fórmula pura
        else if (w >= 640) screenFactor.value = 1.015;    // tablet: ajuste mínimo
        else screenFactor.value = 1.0;                     // móvil: sin labels, fórmula pura
    }

    const progressFraction = computed(() => {
        const total = props.steps.length;
        const current = props.currentStep;
        if (total <= 1) return 0;
        if (current >= total) return 1;
        const base = (current - 1) / (total - 1);
        return current > 1 ? Math.min(1, base * screenFactor.value) : 0;
    });

    onMounted(() => {
        computeAdjust();
        window.addEventListener('resize', computeAdjust);
        nextTick(() => computeAdjust());
    });

    onUnmounted(() => {
        window.removeEventListener('resize', computeAdjust);
    });
</script>


<style scoped>
.stepper-admin-steps-row {
    --circle-size: calc(clamp(26px, 5vw, 40px) - 5px);
}

.stepper-admin-circle {
    width: var(--circle-size);
    height: var(--circle-size);
    font-size: clamp(12px, 3vw, 20px);
}

.stepper-admin-circle--future {
    background-color: #fff;
    border-color: #ddd;
}

.stepper-admin-circle--active {
    background-color: #b7102a;
    border-color: #b7102a;
    font-size: clamp(12px, 3vw, 18px);
}

.stepper-admin-circle--active .stepper-admin-number {
    color: #ffffff;
}

.stepper-admin-check {
    font-size: clamp(10px, 2.5vw, 16px) !important;
    color: #ffffff;
}

.stepper-admin-number {
    font-size: clamp(10px, 2.5vw, 16px);
    color: #333;
}

.stepper-admin-label {
    font-size: clamp(9px, 2.3vw, 14px);
}

/* Tablet: reducir para igualar anchos */
@media (max-width: 1024px) {
    .stepper-admin-label {
        font-size: 9px;
    }
}

/* ── Líneas ── */
.stepper-admin-line-bg,
.stepper-admin-line-progress {
    left: calc(var(--circle-size) / 2);
    right: calc(var(--circle-size) / 2);
    top: calc(var(--circle-size) / 2 - 2px);
}

.stepper-admin-line-progress {
    right: auto;
    width: calc(var(--progress-fraction) * (100% - var(--circle-size)));
    background-color: #b7102a;
    transition: width 0.5s ease-in-out;
}

/* ── Pulsing ── */
.stepper-admin-circle--pulsing {
    position: relative;
}

.stepper-admin-circle--pulsing::after {
    content: '';
    position: absolute;
    width: clamp(32px, 5vw, 44px);
    height: clamp(32px, 5vw, 44px);
    border-radius: 50%;
    border: 2px solid #b7102a;
    animation: stepper-admin-pulse 2s infinite;
}

@keyframes stepper-admin-pulse {
    0%   { transform: scale(1);   opacity: 0.7; }
    100% { transform: scale(1.2); opacity: 0;   }
}

@media (max-width: 639px) {
    .stepper-admin-wrapper {
        -webkit-overflow-scrolling: touch;
        scrollbar-width: none;
    }
    .stepper-admin-wrapper::-webkit-scrollbar { display: none; }
}

@media (max-width: 600px) {
    .stepper-admin-steps-row {
        --circle-size: calc(clamp(24px, 5vw, 32px) - 4px);
    }
    .stepper-admin-circle {
        font-size: clamp(11px, 2.5vw, 16px);
    }
    .stepper-admin-circle--active {
        font-size: clamp(12px, 3vw, 17px);
    }
    .stepper-admin-label {
        display: none;
    }
    .stepper-admin-number {
        font-size: 13px;
    }
}
</style>
